import React, { useEffect, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import {
  NavLink,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { getAccessToken, removeAccessToken } from "../api";
import LazyLearningIndex from "../learning/pages/LazyLearningIndex";
import TestPage from "../pages/TestPage";
import { loadCommissions } from "../redux/commissions/commissionsActions";
import { isValidURL } from "../tools/func";
import AccountPendingApproval from "./cmp/AccountPendingApproval";
import EmailAddressNotVerified from "./cmp/EmailAddressNotVerified";
import LogoRender from "./cmp/LogoRender";
import LazyAnalyticsPage from "./pages/analytics/LazyAnalyticsPage";
import MobileAppsFooter from "./pages/cmp/MobileAppsFooter";
import StoreSwitcher from "./pages/cmp/StoreSwitcher";
import ContactUs from "./pages/ContactUs";
import CustomPageLazy from "./pages/custom-page/CustomPageLazy";
import IFrameComponent from "./pages/help/components/IFrameComponent";
import LazyHelpPage from "./pages/help/LazyHelpPage";
import Home from "./pages/Home";
import LazyStoreIndex from "./pages/inportalstore/LazyStoreIndex";
import LandingFooter from "./pages/landing/components/LandingFooter";
import LazySettingsPage from "./pages/LazySettingsPage";
import LazyMessageCenterIndex from "./pages/message-center/LazyMessageCenterIndex";
import LazyMLMPage from "./pages/mlm/LazyMLMPage";
import OffersPage from "./pages/OffersPage";
import LazyPageBuilder from "./pages/pagebuilder/LazyPageBuilder";
import LazyShowcaseIndex from "./pages/party-links/PartyLinksIndexLazy";
import Payments from "./pages/Payments";
import LazyW8BEN from "./pages/taxforms/w8-ben/LazyW8BEN";
import LazyW9Page from "./pages/taxforms/w9/LazyW9Page";
import LazyTipaltiIndex from "./pages/tipalti/LazyTipaltiIndex";
import TransactionsIndexLazy from "./pages/transactions/TransactionsIndexLazy";

const CRMIndex = React.lazy(
  () => import("./pages/marketing-tools/crm/CRMIndex"),
);
const LazyBirthdayWidget = React.lazy(() => import("./cmp/BirthdayWidget"));
const PublicShopOptions = React.lazy(
  () => import("./pages/edit-shop/PublicShopOptions"),
);
const LazyFreeGift = React.lazy(
  () => import("./pages/free-gift/FreeGiftIndex"),
);
export default function DashboardContainer() {
  const aff_inportalstore_enabled = useSelector(
    (state) => state.settings.aff_inportalstore_enabled,
  );
  const navigate = useNavigate();
  return (
    <Routes>
      <Route element={<Dashboard navigate={navigate} />}>
        <Route path={"payments"} element={<Payments />} />
        <Route path={"analytics"} element={<LazyAnalyticsPage />} />
        <Route path={"products"} element={<OffersPage />} />
        <Route path={"settings/legal/w9/*"} element={<LazyW9Page />} />
        <Route path={"settings/legal/w8/*"} element={<LazyW8BEN />} />
        <Route path={"settings"} element={<LazySettingsPage />} />
        <Route path={"network/*"} element={<LazyMLMPage />} />
        <Route path="contacts/*" element={<CRMIndex />} />
        <Route
          path={"/mirapage"}
          element={
            <IFrameComponent
              src={"https://mirapage.com/goaffpro-sso"}
              minHeight={"600px"}
            />
          }
        />
        <Route
          path={"/linkything"}
          element={
            <IFrameComponent
              src={"https://linkything.com/goaffpro-sso"}
              minHeight={"600px"}
            />
          }
        />
        <Route path={"help"} element={<LazyHelpPage />} />
        <Route path={"mypages/*"} element={<LazyPageBuilder />} />
        {aff_inportalstore_enabled && (
          <Route path={"store"} element={<LazyStoreIndex />} />
        )}
        <Route path={"edit-shop"} element={<PublicShopOptions />} />
        <Route path={"pages/:page_id"} element={<CustomPageLazy />} />
        <Route path={"partner-contact"} element={<ContactUs />} />
        <Route path={"transactions"} element={<TransactionsIndexLazy />} />
        <Route path={"tipalti/*"} element={<LazyTipaltiIndex />} />
        <Route path={"party-links"} element={<LazyShowcaseIndex />} />
        <Route path={"/message-center"} element={<LazyMessageCenterIndex />} />
        <Route path={"/test"} element={<TestPage />} />
        <Route path={"gift/*"} element={<LazyFreeGift />} />
        <Route path={"learning/*"} element={<LazyLearningIndex />} />
        <Route index path={"*"} element={<Home />} />
      </Route>
    </Routes>
  );
}

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    notify_affiliate_email_verification,
    aff_logo_on_dashboard,
    email_verified,
    status,
    aff_analytics_enabled,
    aff_inportalstore_enabled,
    aff_show_transactions,
    ref_code,
    coupon,
    name,
    email,
    affiliate_id,
    affiliate_portal_config: portalCfg,
    dashboard_bg_color,
    active_tab_color,
    inactive_tab_color,
    isEmbedded,
    isShopifyAutoLogin,
    site_id,
    aff_home_enabled,
    aff_payments_enabled,
    aff_settings_enabled,
    aff_products_enabled,
    aff_mirapage_enabled,
    aff_linkything_enabled,
    enable_page_builder,
    aff_network_tab_show,
    page_sections,
    showcaseConfig,
    aff_extra_tabs: extraTabsString,
    tab_order,
  } = useSelector((state) => state.settings);
  const extraTabs = JSON.parse(extraTabsString || "[]");
  const { logo_link, logo_align } =
    page_sections?.default_navigation?.props || {};
  useEffect(() => {
    dispatch(loadCommissions());
  }, [dispatch]);
  const affiliate_portal_config = portalCfg ? JSON.parse(portalCfg) : {};

  const handleLogout = () => {
    removeAccessToken();
    navigate("/logout");
  };

  const getTabs = () => {
    let tabs = [];
    if (aff_home_enabled)
      tabs.push({ to: "/", id: "navlinkHome", exact: true });
    if (aff_products_enabled)
      tabs.push({ to: "/products", id: "Marketing Tools" });
    if (aff_mirapage_enabled) tabs.push({ to: "/mirapage", id: "Mirapage" });
    if (aff_linkything_enabled)
      tabs.push({ to: "/linkything", id: "Linkything" });
    if (showcaseConfig) tabs.push({ to: "/party-links", id: "Party Links" });
    if (enable_page_builder) tabs.push({ to: "/mypages", id: "Pages" });
    if (aff_analytics_enabled) tabs.push({ to: "/analytics", id: "Analytics" });
    if (aff_network_tab_show) tabs.push({ to: "/network", id: "Network" });
    if (aff_inportalstore_enabled) tabs.push({ to: "/store", id: "Store" });
    if (aff_show_transactions)
      tabs.push({ to: "/transactions", id: "Transactions" });
    if (aff_payments_enabled)
      tabs.push({ to: "/payments", id: "navlinkPayments" });
    extraTabs
      .filter(
        (item) => String(item.page_status) === "1" && String(item.title).trim(),
      )
      .forEach((item) => {
        const isFullUrl = isValidURL(item.id);
        const u = isFullUrl ? item.id : `/pages/${item.id}`;
        const to = u
          .replace("{{access_token}}", getAccessToken())
          .replace("{{ref_code}}", ref_code)
          .replace("{{coupon}}", coupon)
          .replace("{{name}}", name)
          .replace("{{email}}", email)
          .replace("{{affiliate_id}}", affiliate_id)
          .replace("{{site_id}}", site_id);
        tabs.push({
          to,
          id: item.title,
          target: isFullUrl ? "_blank" : "_self",
        });
      });

    if (aff_settings_enabled)
      tabs.push({ to: "/settings", id: "navlinkSettings" });
    if (tab_order) {
      tabs = tabs.sort((a, b) => {
        const idx1 = tab_order.indexOf(a.id);
        const idx2 = tab_order.indexOf(b.id);
        const idy1 = idx1 > -1 ? idx1 : tab_order.length;
        const idy2 = idx2 > -1 ? idx2 : tab_order.length;
        return idy1 - idy2;
      });
    }
    return tabs;
  };

  const tabs = getTabs();
  const bgColorStyle = { backgroundColor: dashboard_bg_color || "inherit" };
  const inactiveStyle = inactive_tab_color ? { color: inactive_tab_color } : {};
  return (
    <div
      className={`${
        isEmbedded || dashboard_bg_color ? "" : "bg-light "
      } min-vh-100`}
      style={bgColorStyle}
    >
      <div
        className={`${
          affiliate_portal_config.nav_as_sidebar
            ? "container-fluid"
            : "container"
        } px-0 py-3`}
        style={{ position: "relative" }}
      >
        {!isEmbedded && <StoreSwitcher />}
        {aff_logo_on_dashboard && (
          <div
            className={`px-3 mt-2 ${
              logo_align === "center"
                ? "text-center"
                : logo_align === "right"
                  ? "text-end"
                  : "text-start"
            }`}
          >
            <LogoRender height={80} />
          </div>
        )}
      </div>
      <div
        className={`${
          affiliate_portal_config.nav_as_sidebar
            ? "container-fluid"
            : "container px-0 "
        } ${isEmbedded ? "" : " pt-4 pb-4"}`}
      >
        {!email_verified && notify_affiliate_email_verification && (
          <EmailAddressNotVerified />
        )}
        {status === "pending" && <AccountPendingApproval />}
        {status === "blocked" && <AccountBanned />}
        <DashboardNavigation
          tabs={tabs}
          isShopifyAutoLogin={isShopifyAutoLogin}
        />
        <IsIsBirthday />
        {affiliate_portal_config.nav_as_sidebar ? (
          <div className="row">
            <div className="col-md-3 d-none d-md-block">
              <RenderTabs
                sidebar={affiliate_portal_config.nav_as_sidebar}
                tabs={tabs}
                isShopifyAutoLogin={isShopifyAutoLogin}
                inactiveStyle={inactiveStyle}
                inactive_tab_color={inactive_tab_color}
                active_tab_color={active_tab_color}
                handleLogout={handleLogout}
              />
            </div>
            <div className="col-md-9">
              <div className="dashboard-content-area">
                <DashboardRoutes isEmbedded={isEmbedded} inSidebar />
              </div>
            </div>
          </div>
        ) : (
          <>
            <RenderTabs
              tabs={tabs}
              isShopifyAutoLogin={isShopifyAutoLogin}
              inactiveStyle={inactiveStyle}
              inactive_tab_color={inactive_tab_color}
              active_tab_color={active_tab_color}
              handleLogout={handleLogout}
            />
            <DashboardRoutes isEmbedded={isEmbedded} />
          </>
        )}
      </div>
      <div className="pb-4" />
      {!isEmbedded ? <LandingFooter inDashboard /> : <MobileAppsFooter />}
      <EmbeddedIFrameSendReferralCodeToParent />
    </div>
  );
};

function IsIsBirthday() {
  const birthday_reward = useSelector(
    (state) => state.settings.birthday_reward,
  );
  const isBirthday = useSelector((state) => {
    const [yob, mob, dob] = (state.settings.date_of_birth || "").split("-");
    return Number(mob) == new Date().getMonth() + 1;
  });
  if (birthday_reward && isBirthday) {
    return <LazyBirthdayWidget />;
  }
}

function openInNewTab(url) {
  const result = window.open(url, "_blank");
  if (!result) {
    if (window.parent !== window.self) {
      window.parent.postMessage(
        {
          type: "open_in_new_tab",
          url: url,
        },
        "*",
      );
    } else {
      window.location.href = url;
    }
  }
}

function DashboardNavigation({ tabs, isShopifyAutoLogin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const doNav = (e) => {
    if (isValidURL(e.target.value)) {
      openInNewTab(e.target.value);
    } else {
      navigate(e.target.value);
    }
  };
  const navValue = useMemo(() => {
    const foundPath = tabs.find((item) => {
      return location.pathname.startsWith(item.to) && !item.exact;
    });

    return foundPath ? foundPath.to : location.pathname;
  }, [tabs, location]);
  return (
    <>
      <select
        className={"form-control mb-2 d-block d-md-none border-bottom"}
        value={navValue}
        onChange={doNav}
      >
        {tabs.map((item) => {
          return (
            <FormattedMessage key={String(item.id)} id={item.id}>
              {(text) => <option value={item.to}>{text}</option>}
            </FormattedMessage>
          );
        })}
        <option value={"/"}>---</option>
        {!isShopifyAutoLogin ? (
          <FormattedMessage id={"logout"}>
            {(text) => <option value="/logout">{text}</option>}
          </FormattedMessage>
        ) : null}
      </select>
    </>
  );
}

function DashboardRoutes({ isEmbedded, inSidebar }) {
  return (
    <div
      className={`bg-white p-2 p-md-4 ${
        inSidebar
          ? ""
          : `border-right border-left border-bottom rounded-bottom${!isEmbedded ? " shadow" : ""}`
      }`}
    >
      <Outlet />
    </div>
  );
}

function RenderTabs({
  handleLogout,
  sidebar,
  tabs,
  active_tab_color,
  inactive_tab_color,
  inactiveStyle,
  isShopifyAutoLogin,
}) {
  const inactive_tab_style = inactive_tab_color
    ? { color: inactive_tab_color }
    : {};
  const active_tab_style = active_tab_color
    ? { color: `${active_tab_color} !important` }
    : {};
  if (sidebar) {
    return (
      <nav className="dashboard-sidebar d-flex flex-column">
        {tabs.map((item) =>
          item.target === "_blank" ? (
            <a
              href={item.to.replace("{{access_token}}", getAccessToken())}
              target={item.target}
              className="sidebar-navlink"
              style={inactive_tab_style}
              key={item.to}
            >
              <FormattedMessage id={item.id} />
            </a>
          ) : (
            <NavLink
              key={item.to}
              end={item.exact}
              className={({ isActive }) =>
                `sidebar-navlink${isActive ? " active" : ""}`
              }
              style={({ isActive }) =>
                isActive ? active_tab_style : inactive_tab_style
              }
              to={item.to}
            >
              <FormattedMessage id={item.id} />
            </NavLink>
          ),
        )}
        {!isShopifyAutoLogin && (
          <div className="sidebar-logout-area">
            <button
              className="btn btn-link btn-sm text-muted"
              onClick={handleLogout}
              style={inactive_tab_style}
            >
              <FormattedMessage id={"logout"} />
            </button>
          </div>
        )}
      </nav>
    );
  } else {
    return (
      <ul className="nav nav-tabs d-none d-md-flex">
        {tabs.map((item) => (
          <li key={item.to} className="nav-item">
            <NavLink
              className={({ isActive }) =>
                `nav-link ${isActive ? "active" : ""}`
              }
              end={item.exact}
              target={item.target}
              to={item.to}
              style={({ isActive }) =>
                isActive ? active_tab_style : inactive_tab_style
              }
            >
              <FormattedMessage id={item.id} />
            </NavLink>
          </li>
        ))}
        {!isShopifyAutoLogin ? (
          <>
            <li
              className={"justify-content-end d-none d-md-flex"}
              style={{ flex: 1 }}
            >
              <button
                className={"px-0 btn btn-link"}
                onClick={handleLogout}
                style={inactiveStyle}
              >
                <FormattedMessage id={"logout"} />
              </button>
            </li>
          </>
        ) : null}
      </ul>
    );
  }
}

function AccountBanned() {
  return (
    <p className={"alert alert-danger"}>
      <FormattedMessage id={"accountBannedMessage"} />
    </p>
  );
}

function EmbeddedIFrameSendReferralCodeToParent() {
  const ref_code = useSelector((state) => state.settings.ref_code);
  useEffect(() => {
    if (window.self !== window.parent && ref_code) {
      window.parent.postMessage(
        {
          type: "ref_code",
          ref: ref_code,
        },
        "*",
      );
    }
  }, [ref_code]);
  return null;
}
